import { useState } from "react";
import { useUIStore } from "../../store/uiStore";
import { drawerPalette, type DrawerPalette } from "../../lib/drawerTheme";

interface Props {
  pHat:           number | null;
  pTildeDisplay:  number | null;
  groupThreshold: number | null;
  alertTriggered: boolean | null;
}

export function RiskPanel({ pHat, pTildeDisplay, groupThreshold, alertTriggered }: Props) {
  const theme = useUIStore((s) => s.theme);
  const palette = drawerPalette(theme);
  const pHatPct = pHat != null ? Math.min(1, pHat) * 100 : 0;
  const pTildeDisplayPct = pTildeDisplay != null ? pTildeDisplay * 100 : 0;

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
        <h3 style={{ margin: 0, fontSize: 13, fontWeight: 600, color: palette.bodyText }}>
          Risk score
        </h3>
        <RiskScoreInfo palette={palette} />
      </div>

      <RiskRowRaw
        label="Raw p̂"
        value={pHat}
        pct={pHatPct}
        thresholdPct={groupThreshold != null ? groupThreshold * 100 : null}
        fill="#9ca3af"
        palette={palette}
      />
      <RiskRow
        label="Adjusted p̃"
        value={pTildeDisplay}
        pct={pTildeDisplayPct}
        fill="#dc2626"
        palette={palette}
      />

      {alertTriggered && (
        <div
          style={{
            marginTop: 8,
            padding: "4px 8px",
            background: palette.noticeWarnBg,
            color: palette.noticeWarnText,
            border: `1px solid ${palette.noticeWarnBorder}`,
            borderRadius: 4,
            fontSize: 11,
            fontWeight: 500,
            display: "inline-block",
          }}
        >
          ⚠ VLM verification triggered
          {groupThreshold != null && pHat != null && ` (raw ${pHat.toFixed(3)} ≥ Q threshold ${groupThreshold.toFixed(3)})`}
        </div>
      )}
    </div>
  );
}

function RiskRowRaw({
  label, value, pct, thresholdPct, fill, palette,
}: {
  label: string;
  value: number | null;
  pct: number;
  thresholdPct: number | null;
  fill: string;
  palette: DrawerPalette;
}) {
  return (
    <div style={{ marginBottom: 6 }}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 2 }}>
        <span style={{ color: palette.bodyText }}>{label}</span>
        <span style={{ fontFamily: "ui-monospace, SFMono-Regular, monospace", color: palette.mutedText }}>
          {value != null ? value.toFixed(3) : "n/a"}
        </span>
      </div>
      <div style={{ height: 6, background: palette.barTrackBg, borderRadius: 3, overflow: "hidden", position: "relative" }}>
        <div style={{ width: `${pct}%`, height: "100%", background: fill }} />
        {thresholdPct != null && (
          <div
            style={{
              position: "absolute",
              left: `${thresholdPct}%`,
              top: 0,
              width: 2,
              height: "100%",
              background: "#ef4444",
              transform: "translateX(-50%)",
            }}
          />
        )}
      </div>
      {thresholdPct != null && (
        <div style={{ fontSize: 10, color: palette.mutedText, marginTop: 2 }}>
          Threshold: {(thresholdPct).toFixed(1)}%
        </div>
      )}
    </div>
  );
}

function RiskRow({
  label, value, pct, fill, palette,
}: { label: string; value: number | null; pct: number; fill: string; palette: DrawerPalette }) {
  return (
    <div style={{ marginBottom: 6 }}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 2 }}>
        <span style={{ color: palette.bodyText }}>{label}</span>
        <span style={{ fontFamily: "ui-monospace, SFMono-Regular, monospace", color: palette.mutedText }}>
          {value != null ? value.toFixed(3) : "n/a"}
        </span>
      </div>
      <div style={{ height: 6, background: palette.barTrackBg, borderRadius: 3, overflow: "hidden" }}>
        <div style={{ width: `${pct}%`, height: "100%", background: fill }} />
      </div>
      {value != null && (
        <div style={{ fontSize: 10, color: palette.mutedText, marginTop: 2 }}>
          Display transform (always 0–100%)
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Hoverable info button + popover.
// Bands kept in sync with riskLabel() in src/lib/colormap.ts.
// ---------------------------------------------------------------------------
function RiskScoreInfo({ palette }: { palette: DrawerPalette }) {
  const [open, setOpen] = useState(false);
  const show = () => setOpen(true);
  const hide = () => setOpen(false);

  return (
    <span style={{ position: "relative", display: "inline-flex" }}>
      <button
        type="button"
        aria-label="What does the risk score mean?"
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        style={{
          width: 16,
          height: 16,
          padding: 0,
          fontSize: 11,
          fontWeight: 600,
          lineHeight: "14px",
          color: palette.infoBtnText,
          background: palette.infoBtnBg,
          border: `1px solid ${palette.infoBtnBorder}`,
          borderRadius: "50%",
          cursor: "help",
        }}
      >
        i
      </button>
      {open && (
        <div
          role="tooltip"
          style={{
            position: "absolute",
            top: "120%",
            left: 0,
            zIndex: 10,
            width: 300,
            padding: "8px 10px",
            background: palette.tooltipBg,
            color: palette.tooltipText,
            border: `1px solid ${palette.tooltipBorder}`,
            borderRadius: 6,
            fontSize: 11,
            lineHeight: 1.5,
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 4 }}>What this score means</div>
          <p style={{ margin: "0 0 6px" }}>
            <span style={{ color: palette.accentLink, fontWeight: 600 }}>Raw p̂</span> — model's prediction
            from weather and fuel patterns alone.
          </p>
          <p style={{ margin: "0 0 6px" }}>
            <span style={{ color: "#dc2626", fontWeight: 600 }}>Adjusted p̃</span> — same risk, adjusted
            for community vulnerability. Higher than raw = harder to evacuate or recover from a missed warning.
            <b> Always 0 to 100%.</b>
          </p>
          <p style={{ margin: "0 0 6px" }}>
            <span style={{ fontWeight: 600 }}>Alert threshold</span> — VLM satellite verification fires
            when raw p̂ clears this community's specific threshold (red marker). Vulnerable communities have
            lower thresholds; borderline cells get a second look.
          </p>
          <div style={{ fontWeight: 600, marginTop: 6, marginBottom: 2 }}>How adjustment works</div>
          <p style={{ margin: "0 0 4px", fontSize: 10 }}>
            Each community's historical recall (Q1 most vulnerable: 68.7%, Q5 least: 90.8%) informs
            its per-group threshold. Q1=34.4%, Q2=35.0%, Q3=34.9%, Q4=42.9%, Q5=45.4%.
          </p>
          <div style={{ fontWeight: 600, marginTop: 6, marginBottom: 2 }}>Risk bands</div>
          <ul style={{ margin: 0, paddingLeft: 14 }}>
            <li><BandSwatch color="#91cf60" />&nbsp;<b>Low</b> &nbsp;&lt; 40%</li>
            <li><BandSwatch color="#fee08b" />&nbsp;<b>Moderate</b> &nbsp;40–60%</li>
            <li><BandSwatch color="#fc8d59" />&nbsp;<b>High</b> &nbsp;60–80%</li>
            <li><BandSwatch color="#d73027" />&nbsp;<b>Critical</b> &nbsp;≥ 80%</li>
          </ul>
        </div>
      )}
    </span>
  );
}

function BandSwatch({ color }: { color: string }) {
  return (
    <span
      aria-hidden
      style={{
        display: "inline-block",
        width: 9,
        height: 9,
        borderRadius: 2,
        background: color,
        verticalAlign: "middle",
      }}
    />
  );
}
